import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Users,
    MessageSquare,
    Hash,
    Zap,
    TrendingUp,
    TrendingDown
} from 'lucide-react';
import { DashboardMetrics } from '@/types/dashboard';

interface MetricsCardsProps {
    metrics?: DashboardMetrics;
}

const MetricsCards: React.FC<MetricsCardsProps> = ({ metrics }) => {

    if (!metrics) return null;

    const formatNumber = (num: number) => {
        if (num >= 1000000) {
            return `${(num / 1000000).toFixed(1)}M`;
        }
        if (num >= 1000) {
            return `${(num / 1000).toFixed(1)}K`;
        }
        return num.toString();
    };

    const cards = [
        {
            title: 'Active Users',
            value: metrics.activeUsers,
            icon: <Users className="h-4 w-4" />,
            trend: '+12%',
            trendUp: true,
        },
        {
            title: 'Total Threads',
            value: metrics.threads,
            icon: <Hash className="h-4 w-4" />,
            trend: '+8%',
            trendUp: true,
        },
        {
            title: 'Messages',
            value: metrics.messages,
            icon: <MessageSquare className="h-4 w-4" />,
            trend: '+15%',
            trendUp: true,
        },
        {
            title: 'Total Tokens',
            value: metrics.inputTokens + metrics.outputTokens + metrics.cachedTokens,
            icon: <Zap className="h-4 w-4" />,
            trend: '+22%',
            trendUp: true,
        },
    ];

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {cards.map((card, index) => (
                <Card key={index} className="hover:shadow-md transition-shadow">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium text-foreground">
                            {card.title}
                        </CardTitle>
                        <div className="text-primary">
                            {card.icon}
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-muted-foreground">
                            {formatNumber(card.value)}
                        </div>
                        <div className="flex items-center text-xs mt-1">
                            {card.trendUp ? (
                                <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
                            ) : (
                                <TrendingDown className="h-3 w-3 text-red-500 mr-1" />
                            )}
                            <span
                                className={card.trendUp ? 'text-green-500' : 'text-red-500'}
                            >
                                {card.trend}
                            </span>
                            <span className="ml-1 text-foreground">
                                from last period
                            </span>
                        </div>
                    </CardContent>
                </Card>
            ))}
        </div>
    );
};

export default MetricsCards;
